import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#om-oss", label: "Om Oss" },
    { href: "#tjanster", label: "Tjänster" },
    { href: "/inspiration", label: "Inspiration", isRoute: true },
    { href: "#omdomen", label: "Omdömen" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const scrollToSection = (e, href, isRoute) => {
    e.preventDefault();
    if (isRoute) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    
    if (!isHomePage) {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-black/80 backdrop-blur-lg border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a
              href="#"
              data-testid="logo-link"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.preventDefault();
                if (isHomePage) {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  navigate("/");
                }
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/kkb2qh1x_Optimal%20El%20Logga.png" 
                alt="Optimal EL Stockholm" 
                className="h-10 md:h-12 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm tracking-wide"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                    className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm tracking-wide"
                  >
                    {link.label}
                  </a>
                )
              ))}
            </nav>

            {/* CTA & Mobile Menu Button */}
            <div className="flex items-center gap-4">
              <a
                href="tel:0722930914"
                data-testid="header-phone-link"
                className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
              >
                <Phone size={16} />
                <span>072-293 09 14</span>
              </a>
              {isHomePage ? (
                <a
                  href="#kontakt"
                  data-testid="header-cta-button"
                  onClick={(e) => scrollToSection(e, "#kontakt", false)}
                  className="hidden sm:block bg-[#D4AF37] text-black px-6 py-2.5 font-medium text-sm tracking-wide hover:bg-[#C5A028] transition-colors duration-300"
                >
                  Få Offert
                </a>
              ) : (
                <Link
                  to="/#kontakt"
                  data-testid="header-cta-button"
                  onClick={() => {
                    navigate("/");
                    setTimeout(() => {
                      const el = document.querySelector("#kontakt");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                  className="hidden sm:block bg-[#D4AF37] text-black px-6 py-2.5 font-medium text-sm tracking-wide hover:bg-[#C5A028] transition-colors duration-300"
                >
                  Få Offert
                </Link>
              )}
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-white p-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center"
          >
            <nav className="flex flex-col items-center gap-8">
              {navLinks.map((link, index) => (
                link.isRoute ? (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-white text-2xl font-light tracking-wide hover:text-[#D4AF37] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-white text-2xl font-light tracking-wide hover:text-[#D4AF37] transition-colors"
                  >
                    {link.label}
                  </motion.a>
                )
              ))}
              <motion.a
                href="#kontakt"
                onClick={(e) => scrollToSection(e, "#kontakt", false)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 bg-[#D4AF37] text-black px-8 py-4 font-medium tracking-wide"
              >
                Få Offert
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
